import React, { useState } from "react";
import { Heading } from "@UI/Components/Heading/Heading";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Box } from "@UI/Components/Box/Box";
import { Text } from "@UI/Components/Text/Text";
import { Card } from "@UI/Components/Card/Card";
import { Flex } from "@UI/Components/Flex/Flex";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { TriangleExclamation } from "@UI/Components/Icons/TriangleExclamation";
import { Icon } from "@UI/Components/Icon/Icon";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useProviderServicesCount } from "@modules/ServiceProviders/hooks/useProviderServicesCount";
import { GenericAlert } from "@UI/Components/GenericAlert/GenericAlert";
import { useRealisationRates } from "@modules/ServiceProviders/hooks/useRealisationRate";
import { JobsInProgressModal } from "./JobsInProgressModal";
import { useServiceProvider } from "@modules/ServiceProviders/hooks/useServiceProvider";
import messages from "./messages";

export const RealisationRateImpact = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useMediaQuery();
  const { currentUser } = useCurrentUser({
    includes: ["serviceProvider"],
  });

  const { serviceProvider } = useServiceProvider(
    currentUser?.serviceProvider?.id,
    {
      includes: ["user"],
    }
  );

  const { servicesCount, isLoading } = useProviderServicesCount(
    currentUser?.serviceProvider?.id
  );

  const { data: realisationRateMetrics } = useRealisationRates(
    currentUser?.serviceProvider?.id
  );

  const statusRealisation = realisationRateMetrics?.metric?.currentLevel;

  const availableJobs = Math.max(
    0,
    (servicesCount?.maxInProgressServices ?? 0) -
      (servicesCount?.inProgressServicesCount ?? 0)
  );

  const isBlocked = statusRealisation === "blocked" || availableJobs <= 0;

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Heading textAlign="start" fontSize={{ base: "2xl", lg: "4xl" }}>
        <FormattedMessage {...messages.impactTitle} />
      </Heading>
      <Box mt={2} mb={5}>
        <Text as="span" fontSize="md">
          <FormattedMessage {...messages.impactInfos} />
        </Text>
      </Box>
      {(statusRealisation === "blocked" || statusRealisation === "warning") && (
        <GenericAlert status="warning">
          <FormattedMessage {...messages[statusRealisation]} />
        </GenericAlert>
      )}
      <Card
        w="full"
        p={{ base: 4, md: 5 }}
        mt={5}
        cursor={
          statusRealisation !== "blocked" && !serviceProvider?.superTalent
            ? "pointer"
            : ""
        }
        onClick={() => {
          statusRealisation !== "blocked" && !serviceProvider?.superTalent
            ? setIsOpen(true)
            : null;
        }}
        role="group"
        _hover={{
          shadow:
            statusRealisation !== "blocked" && !serviceProvider?.superTalent
              ? "lg"
              : "",
        }}
      >
        <Flex flexDirection="row" justifyContent="space-between">
          <Box w="auto">
            <Text as="span" fontSize={{ base: "sm", md: "md" }}>
              <FormattedMessage {...messages.jobsInProgress} />
            </Text>
            <Flex mt={1} mb={1} alignItems="center">
              <Text
                as="span"
                fontWeight={700}
                color="black"
                fontSize={{ base: "xl", md: "3xl" }}
              >
                {servicesCount?.inProgressServicesCount}
              </Text>
            </Flex>
            <Flex
              backgroundColor={isBlocked ? "red.50" : "green.50"}
              justifyContent="space-around"
              alignItems="center"
              px="8px"
              py="2px"
              borderRadius="100px"
              w="fit-content"
            >
              {isBlocked && (
                <Icon
                  as={TriangleExclamation}
                  w={4}
                  h={4}
                  fontWeight={700}
                  color="red.700"
                />
              )}
              <Text
                color={isBlocked ? "red.700" : "green.700"}
                as="span"
                ml={1}
                fontWeight={400}
                fontSize="sm"
              >
                {serviceProvider?.superTalent ? (
                  <FormattedMessage {...messages.unlimited_jobs} />
                ) : (
                  <FormattedMessage
                    {...messages.jobsAvailable}
                    values={{
                      maxInProgressServices: availableJobs,
                    }}
                  />
                )}
              </Text>
            </Flex>
          </Box>
          {statusRealisation !== "blocked" && !serviceProvider?.superTalent && (
            <Box
              alignSelf={{ base: "center", md: "flex-end" }}
              cursor="pointer"
              width="auto"
            >
              {!isMobile ? (
                <Text
                  as="span"
                  textDecoration="underline"
                  _groupHover={{ color: "gray.800" }}
                >
                  <FormattedMessage
                    {...messages.seeMore}
                    values={{
                      RightIcon: () => (
                        <Icon as={ArrowRight} w={4} h={4} fontWeight={700} />
                      ),
                    }}
                  />
                </Text>
              ) : (
                <Icon as={ArrowRight} w={4} h={4} fontWeight={700} />
              )}
            </Box>
          )}
        </Flex>
      </Card>
      <JobsInProgressModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        isMobile={isMobile}
      />
    </>
  );
};
